import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { distinctUntilChanged, take } from 'rxjs/operators';

import {
  GlobalDialogService,
  MaintenanceBypassService,
  MaintenanceService,
} from '@customer-portal/core';
import { QualtricsSurveyFeedbackStoreService } from '@customer-portal/data-access/global';
import { SettingsCoBrowsingStoreService } from '@customer-portal/data-access/settings';
import { environment } from '@customer-portal/environments';
import { APP_TITLE, AppPagesEnum } from '@customer-portal/shared/constants';
import { AuthService } from '@customer-portal/shared/services/auth/auth.service';

@Component({
  imports: [RouterModule, CommonModule],
  selector: 'customer-portal-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private renderer = inject(Renderer2);
  private maintenanceService = inject(MaintenanceService);
  private maintenanceBypassService = inject(MaintenanceBypassService);
  private globalDialogService = inject(GlobalDialogService);
  private confirmationService = inject(ConfirmationService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private qualtricsFeedbackStoreService = inject(
    QualtricsSurveyFeedbackStoreService,
  );
  private settingsCoBrowsingStoreService = inject(
    SettingsCoBrowsingStoreService,
  );
  title = APP_TITLE;

  constructor(
    private primengConfig: PrimeNG,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    document.title = APP_TITLE;

    this.setPrimeNGTranslations();
    this.setupLanguageListener();
    this.initializeMaintenanceCheck();

    if (
      environment.qualtricsAlwaysOnFeedbackEnabled &&
      !this.settingsCoBrowsingStoreService.isDnvUser()
    ) {
      this.qualtricsFeedbackStoreService.loadQualtricsSurveyDetails();
    }
  }

  private setPrimeNGTranslations(): void {
    this.translocoService
      .selectTranslate('noResultsFound')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translation) => {
        this.primengConfig.setTranslation({
          emptySearchMessage: translation,
        });
      });
  }

  private setupLanguageListener(): void {
    this.translocoService.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang) => {
        this.renderer.setAttribute(document.body, 'lang', lang);
      });
    const currentLang = this.translocoService.getActiveLang();

    if (currentLang) {
      this.renderer.setAttribute(document.body, 'lang', currentLang);
    }
  }

  private initializeMaintenanceCheck(): void {
    this.maintenanceService.maintenance$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          const prevActive = prev?.active ?? false;
          const currActive = curr?.active ?? false;
          const prevUpdated = prev?.updatedAt ?? '';
          const currUpdated = curr?.updatedAt ?? '';

          return prevActive === currActive && prevUpdated === currUpdated;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((config) => {
        const currentRoute = this.router.url;
        const bypassed = this.maintenanceBypassService.isBypassed();

        if (
          config?.active &&
          !bypassed &&
          !currentRoute.includes(AppPagesEnum.Maintenance)
        ) {
          this.globalDialogService.closeAll();
          this.confirmationService.close();

          this.router.navigate([AppPagesEnum.Maintenance]);

          this.authService.logout().pipe(take(1)).subscribe();
        } else if (!config?.active) {
          this.maintenanceBypassService.clearBypass();

          if (currentRoute.includes(AppPagesEnum.Maintenance)) {
            window.location.href = '/';
          }
        }
      });

    const currentRoute = this.router.url;

    if (!currentRoute.includes(AppPagesEnum.Maintenance)) {
      this.authService
        .isUserAuthenticatedWithExpiryInfo()
        .pipe(take(1))
        .subscribe((auth) => {
          if (auth.isUserAuthenticated) {
            this.maintenanceService.connectSignalR();
          }
        });
    }
  }
}
